import { useQuery } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import Card from '../../components/ui/Card';
import MachineFilters from '../../components/ui/MachineFilters';
import PageHeader from '../../components/ui/PageHeader';
import { useMachineFilters } from '../../hooks/useMachineFilters';
import { insightsApi, type ComposterInsightRow } from '../../services/insightsApi';

interface SensorConfig {
  field: 'pileTemp' | 'heatPadTemp' | 'avgMoisture' | 'pileLevel';
  label: string;
  unit: string;
  color: string;
  warnAt?: { min?: number; max?: number };
}

const SENSORS: SensorConfig[] = [
  { field: 'pileTemp', label: 'Pile temperature', unit: '°C', color: '#10b981', warnAt: { max: 65 } },
  {
    field: 'heatPadTemp',
    label: 'Heat-pad temperature',
    unit: '°C',
    color: '#f59e0b',
    warnAt: { max: 70 },
  },
  { field: 'avgMoisture', label: 'Average moisture', unit: '%', color: '#0ea5e9', warnAt: { min: 40 } },
  { field: 'pileLevel', label: 'Pile level', unit: 'cm', color: '#8b5cf6' },
];

const DiagnosticsView = () => {
  const f = useMachineFilters({ machineType: 'composter' });
  const q = useQuery({
    queryKey: ['insights', 'composter', f.selectedMachineId, f.from, f.to],
    enabled: Boolean(f.selectedMachineId),
    queryFn: async () =>
      (await insightsApi.composter(f.selectedMachineId as string, f.from, f.to)).data,
  });

  const rows = q.data?.rows ?? [];

  return (
    <>
      <PageHeader
        title="Diagnostics"
        subtitle="Sensor metrics over time, with operational thresholds."
        actions={
          <MachineFilters
            machines={f.machines}
            selectedMachineId={f.selectedMachineId}
            onMachineChange={f.setMachineId}
            from={f.from}
            to={f.to}
            onFromChange={f.setFrom}
            onToChange={f.setTo}
            loading={f.machinesLoading}
          />
        }
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {SENSORS.map((s) => (
          <SensorChart key={s.field} sensor={s} rows={rows} />
        ))}

        <Card title="Load cell weight (latest)" subtitle="Last reading per day">
          <ReactECharts
            option={loadCellOption(rows)}
            style={{ height: 280 }}
            notMerge
            lazyUpdate
          />
        </Card>
      </div>
    </>
  );
};

const SensorChart = ({ sensor, rows }: { sensor: SensorConfig; rows: ComposterInsightRow[] }) => {
  const data = rows.map((r) => r[sensor.field]);
  const valid = data.filter((v): v is number => v != null);
  const avg = valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : null;
  const max = valid.length ? Math.max(...valid) : null;
  const min = valid.length ? Math.min(...valid) : null;

  const markLines = [];
  if (sensor.warnAt?.max != null) {
    markLines.push({
      yAxis: sensor.warnAt.max,
      label: { formatter: 'warn', color: '#dc2626', position: 'end' as const },
      lineStyle: { color: '#dc2626', type: 'dashed' as const },
    });
  }
  if (sensor.warnAt?.min != null) {
    markLines.push({
      yAxis: sensor.warnAt.min,
      label: { formatter: 'min', color: '#dc2626', position: 'end' as const },
      lineStyle: { color: '#dc2626', type: 'dashed' as const },
    });
  }

  const option = {
    grid: { left: 50, right: 20, top: 20, bottom: 30 },
    tooltip: { trigger: 'axis' as const },
    xAxis: {
      type: 'category' as const,
      data: rows.map((r) => r.ts.slice(5)),
      axisLabel: { color: '#64748b' },
    },
    yAxis: {
      type: 'value' as const,
      axisLabel: { color: '#64748b', formatter: `{value} ${sensor.unit}` },
      splitLine: { lineStyle: { color: '#e2e8f0' } },
      scale: true,
    },
    series: [
      {
        type: 'line' as const,
        smooth: true,
        data,
        itemStyle: { color: sensor.color },
        areaStyle: { color: `${sensor.color}1f` },
        markLine: markLines.length ? { silent: true, symbol: 'none' as const, data: markLines } : undefined,
      },
    ],
  };

  return (
    <Card
      title={sensor.label}
      subtitle={
        avg != null
          ? `avg ${avg.toFixed(1)}${sensor.unit} · range ${(min as number).toFixed(0)}–${(max as number).toFixed(0)}${sensor.unit}`
          : 'no data'
      }
    >
      <ReactECharts option={option} style={{ height: 220 }} notMerge lazyUpdate />
    </Card>
  );
};

const loadCellOption = (rows: ComposterInsightRow[]) => ({
  grid: { left: 50, right: 20, top: 20, bottom: 30 },
  tooltip: { trigger: 'axis' as const },
  xAxis: {
    type: 'category' as const,
    data: rows.map((r) => r.ts.slice(5)),
    axisLabel: { color: '#64748b' },
  },
  yAxis: {
    type: 'value' as const,
    axisLabel: { color: '#64748b', formatter: '{value} kg' },
    splitLine: { lineStyle: { color: '#e2e8f0' } },
  },
  series: [
    {
      type: 'bar' as const,
      data: rows.map((r) => r.kgsLoaded),
      itemStyle: { color: '#94a3b8', borderRadius: [4, 4, 0, 0] },
    },
  ],
});

export default DiagnosticsView;
